import {
  AlertCircle as IconAlertCircle,
  AlertOctagon as IconAlertOctagon,
  CashOnDelivery as IconCashOnDelivery,
  InformationCircle as IconInformationCircle,
} from '@ahamove/icons/order';
import type { Path } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import { FloatingLabelNumberInput } from '@ahm/react-ui';
import {
  Alert,
  Button,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { ChevronRight as IconChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { RefObject } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import InfoTooltip from '@/components/info-tooltip';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type NationwidePathCodProps = {
  handleOpenModal: (modalId: any) => void;
  handleInputChange: (field: keyof Path, value: number | string) => void;
  codInputRef: RefObject<HTMLInputElement | null>;
};

export default function NationwidePathCod({
  handleOpenModal,
  handleInputChange,
  codInputRef,
}: NationwidePathCodProps) {
  const {
    control,
    formState: { errors },
    setError,
    setValue,
    trigger,
    clearErrors,
  } = useFormContext();
  const tBooking = useTranslations('booking');
  const { selectedService } = useBookingStore((state) => state);
  const isMobile = useIsMobile();
  const { session } = useAuth();
  const user = session?.user;
  const router = useRouter();

  const handleCodChange = async (value: number | string) => {
    handleInputChange('cod', value);

    if (typeof value === 'number') {
      if (value < 0) {
        setError('cod', {
          type: 'min',
          message: tBooking('location.min_cod_error'),
        });
        return;
      }

      setValue('cod', value);

      await trigger('cod');

      const maxCod = selectedService?.max_cod ?? 0;
      let errorMessage: string | undefined;

      if (value > maxCod) {
        errorMessage = tBooking(
          'location.cod_limits_error_input_for_service_max_cod',
          {
            // service_name: selectedService?.name,
            max_cod: maxCod.toLocaleString('vi-VN'), // Định dạng số theo tiếng Việt
          }
        );
      }

      if (errorMessage) {
        setError('cod', {
          type: 'max',
          message: errorMessage,
        });
      } else {
        clearErrors('cod');
      }
    }
  };

  return (
    <Card radius={0} pb={0}>
      <Card.Section px="md" py={rem(8)}>
        {isMobile ? (
          <Title order={4}>{tBooking('location.cod')}</Title>
        ) : (
          <Text size="lg" fw={700} tt="uppercase" lts={0.5} c="neutral.7">
            {tBooking('location.cod')}
          </Text>
        )}
      </Card.Section>
      <Divider mx={rem(-16)} />

      <Stack gap={rem(10)} py="sm" component={Card.Section}>
        <Controller
          name="cod"
          control={control}
          render={({ field }) => (
            <FloatingLabelNumberInput
              {...field}
              ref={codInputRef}
              mx="md"
              min={0}
              label={tBooking('location.cod')}
              disabled={
                !user?.verified || selectedService?.max_cod === undefined
              }
              rightSection={
                !user?.verified ? (
                  <InfoTooltip
                    label={tBooking('location.cod')}
                    onClick={() => handleOpenModal('cod-add-bank-account')}
                  />
                ) : (
                  <></>
                )
              }
              onClick={() => handleOpenModal('cod-add-bank-account')}
              onChange={(value) => {
                void handleCodChange(value);
              }}
              error={errors.cod?.message as string}
            />
          )}
        />

        {!user?.verified ? (
          <Alert
            icon={<IconAlertCircle />}
            radius={0}
            color="primary"
            styles={{
              root: {
                borderRadius: 0,
                padding: rem(12),
                border: 'none',
                backgroundColor: 'var(--mantine-color-primary-0)',
              },
              icon: {
                marginRight: rem(8),
              },
              title: {
                color: 'var(--mantine-color-neutral-7)',
                fontSize: rem(16),
                lineHeight: rem(24),
                fontWeight: 700,
              },
              message: {
                color: 'var(--mantine-color-neutral-6)',
                fontSize: rem(12),
                lineHeight: rem(16),
                fontWeight: 500,
              },
              body: {
                gap: rem(4),
              },
            }}
          >
            <div>
              {tBooking('location.cod_limits_alert_title')}{' '}
              {formatCurrency(user?.max_cod ?? 1000000)}
            </div>
            <Button
              mt={rem(8)}
              size="compact-sm"
              variant="transparent"
              p={0}
              onClick={() => {
                router.push(`${process.env.NEXT_PUBLIC_APP_V1_URI}/verify`);
              }}
            >
              {tBooking('location.cod_limits_alert_btn_verify_now')}
            </Button>
          </Alert>
        ) : errors.cod ? (
          <Alert
            icon={<IconAlertOctagon />}
            radius={0}
            color="#F4D2D2"
            styles={{
              root: {
                borderRadius: 0,
                padding: rem(12),
                border: 'none',
                backgroundColor: 'var(--mantine-color-primary-0)',
              },
              icon: {
                marginRight: rem(8),
              },
              title: {
                color: 'var(--mantine-color-neutral-7)',
                fontSize: rem(16),
                lineHeight: rem(24),
                fontWeight: 700,
              },
              message: {
                color: 'var(--mantine-color-neutral-6)',
                fontSize: rem(12),
                lineHeight: rem(16),
                fontWeight: 500,
              },
              body: {
                gap: rem(4),
              },
            }}
          >
            {tBooking.rich('location.cod_limits_alert_upto_range_max_cod', {
              link: (chunks: string) => (
                <Link href={`tel:${chunks}`}>
                  <Text size="xs" fw={600} c="neutral.7" component="span">
                    {chunks}
                  </Text>
                </Link>
              ),
            })}
          </Alert>
        ) : null}
        <Button
          radius={0}
          fullWidth
          h="auto"
          px="md"
          onClick={() => handleOpenModal('max-cod')}
          styles={{
            inner: {
              width: '100%',
            },
            label: {
              width: '100%',
            },
          }}
          variant="subtle"
        >
          <Group wrap="nowrap" justify="space-between" w="100%">
            <Group align="flex-end">
              <IconCashOnDelivery width={24} height={24} />
              <Text size="sm" fw={700}>
                {tBooking('location.learn_more_about_COD_limits')}
              </Text>
            </Group>
            <IconChevronRight width={16} height={16} />
          </Group>
        </Button>
        <Alert
          icon={<IconInformationCircle />}
          radius={0}
          color="secondary"
          styles={{
            root: {
              borderRadius: 0,
              padding: rem(12),
              border: 'none',
              backgroundColor: 'white',
            },
            icon: {
              marginRight: rem(8),
            },
            title: {
              color: 'var(--mantine-color-neutral-7)',
              fontSize: rem(16),
              lineHeight: rem(24),
              fontWeight: 700,
            },
            message: {
              color: 'var(--mantine-color-neutral-6)',
              fontSize: rem(12),
              lineHeight: rem(16),
              fontWeight: 500,
            },
            body: {
              gap: rem(4),
            },
          }}
        >
          <Text size="xs" fw={500} c="neutral.5">
            {tBooking('location.cod_limits_descriptions')}
          </Text>
          <Link
            href={'https://www.ahamove.com/pages/guarantee-policy'}
            target="_blank"
          >
            <Text td="underline" size="xs" fw={500} c="neutral.4">
              {tBooking('location.cod_limits_btn_learn_more')}
            </Text>
          </Link>
        </Alert>
      </Stack>
    </Card>
  );
}
